// src/Routes/Route.js
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import Login from "../Pages/AuthPages/Login/Login";
import Dashboard from "../Pages/DashBoard/Dashboard";
import PageNotFound from "../Exceptions/PageNotFound";
import RouteProtector from "./Layouts/IndexPageLayout";
import ForgotPassword from "../Pages/AuthPages/Forgot_Password/ForgotPassword";
import ResetPassword from "../Pages/AuthPages/Reset_Password/ResetPassword";
import Users from "../Pages/Users/Users";
import MyTasks from "../Pages/Tasks/MyTasks";
import Features from "../Components/Footer/Features";
import Pricing from "../Components/Footer/Pricing";
import Faq from "../Components/Footer/Faq";
import Terms from "../Components/Footer/Terms";
import Privacy from "../Components/Footer/Privacy";
import Cookies from "../Components/Footer/Cookies";
import Support from "../Components/Footer/Support";
import SystemStatus from "../Components/Footer/SystemStatus";
import CoverLayout from "../Pages/DataLayouts/CoverLayout";
import ListWithDrawerOutlet from "../Pages/DataLayouts/ListWithDrawerOutlet";

import ChangePassword from "../Pages/Settings/ChangePassword";
import UpdateCredentials from "../Pages/Settings/UpdateCredentials";
import ClientList, {
  DEFAULT_COLUMNS as CLIENT_COLUMNS,
} from "../Pages/Clients/Client/ClientList";
import ClientForm from "../Pages/Clients/Client/ClientForm";

import { Configurations } from "../Pages/OptmisedCode/FormConfigurations";

import PersonalizeDataGrid from "../Components/Table/PersonalizeDataGrid";
import { MAIN_URL } from "../Configurations/Urls";
import NotAuthorized from "../Exceptions/NotAuthorized";
import ClientLicensesList, {
  DEFAULT_COLUMNS as CLIENT_LICENSE_COLUMNS,
} from "../Pages/Clients/ClientLicences/ClientLicensesList";
import OrganizationClientList, {
  DEFAULT_COLUMNS as ORGANIZATION_CLIENT_COLUMNS,
} from "../Pages/Clients/OrganizationClient/OrganizationClientList";
import OrganizationClientContactList, {
  DEFAULT_COLUMNS as CLIENT_CONTACT_COLUMNS,
} from "../Pages/Clients/OrganizationClientContact/OrganizationClientContactList";
import ApplicationUserList, {
  DEFAULT_COLUMNS as APPLICATION_USER_COLUMNS,
} from "../Pages/Clients/ApplicationUsers/ApplicationUserList";
import ClientPaymentList, {
  DEFAULT_COLUMNS as CLIENT_PAYMENT_COLUMNS,
} from "../Pages/Clients/ClientPayments/ClientPaymentList";
import ClientPaymentForm from "../Pages/Clients/ClientPayments/ClientPaymentForm";
import OrganizationClientForm from "../Pages/Clients/OrganizationClient/OrganizationClientForm";
import ClientOrganizationList, {
  DEFAULT_COLUMNS as ORGANIZATION_COLUMNS,
} from "../Pages/Clients/ClientOrganization/ClientOrganizationList";
import ClientOrganizationAdd from "../Pages/Clients/ClientOrganization/ClientOrganizationAdd";
import ClientOrganizationEdit from "../Pages/Clients/ClientOrganization/ClientOrganizationEdit";
import AssignClientApplication from "../Pages/Clients/ClientApplicationAssign/AssignClientApplication";
import AssignOrganizationApplication from "../Pages/Clients/ClientApplicationAssign/AssignOrganizationApplication";
import PermissionsAccess from "../Pages/Permissions/PermissionsAccess";
import ApplicationEditions, {
  EDITION_COLUMNS,
} from "../Pages/Permissions/ApplicationEditions";
import ApplicationEditionForm from "../Pages/Permissions/ApplicationEditionForm";
import UserRolesAccess from "../Pages/Permissions/UserRolesAccess";
import RolePermissionsList, {
  ROLE_COLUMNS,
} from "../Pages/Permissions/RolePermissionsList";
import RolePermissionsForm from "../Pages/Permissions/RolePermissionsForm";
import RolePermissionsView from "../Pages/Permissions/RolePermissionsView";
import {
  PermissionsPersonalizeView,
  UserRolesPersonalizeView,
} from "../Pages/Permissions/AssignmentPersonalizeView";

const drawerList = (listElement, formChildren = []) => ({
  element: <ListWithDrawerOutlet>{listElement}</ListWithDrawerOutlet>,
  children: [{ index: true, element: null }, ...formChildren],
});

const personalizeView = (tableName, dataApi, extra = {}) => ({
  path: "personalize-view",
  element: (
    <PersonalizeDataGrid
      tableName={tableName}
      dataApi={dataApi}
      contextId={extra.contextId || tableName}
      displayTitle={extra.displayTitle}
      dataKey={extra.dataKey}
      configss={extra.configss}
      mapRows={extra.mapRows}
    />
  ),
});

// Routes
const router = () => createBrowserRouter([
  {
    path: "/",
    element: (
      <RouteProtector>
        <Dashboard />
      </RouteProtector>
    ),
  },

  {
    path: "/permissions/access",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      {
        index: true,
        element: <PermissionsAccess />,
      },
      {
        path: "personalize-view",
        element: <PermissionsPersonalizeView />,
      },
    ],
  },

  {
    path: "/editions/access",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<ApplicationEditions />, [
        { path: "add", element: <ApplicationEditionForm mode="add" /> },
        { path: "edit/:id", element: <ApplicationEditionForm mode="edit" /> },
        { path: "view/:id", element: <ApplicationEditionForm mode="view" /> },
        personalizeView(
          "application_editions",
          `${MAIN_URL}/api/core/application-editions`,
          {
            displayTitle: "Application editions",
            dataKey: "editions",
            configss: EDITION_COLUMNS,
            mapRows: (rows) =>
              (rows || []).map((edition) => ({
                ...edition,
                edition_name: edition.edition_name || edition.name || "—",
                is_default_label: edition.is_default ? "Default" : "—",
                is_active_label: edition.is_active !== false ? "Yes" : "No",
              })),
          }
        ),
      ]),
    ],
  },

  {
    path: "/user-roles/access",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      {
        index: true,
        element: <UserRolesAccess />,
      },
      {
        path: "personalize-view",
        element: <UserRolesPersonalizeView />,
      },
    ],
  },

  {
    path: "/role-permissions/access",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<RolePermissionsList />, [
        { path: "add", element: <RolePermissionsForm mode="add" /> },
        { path: "edit/:id", element: <RolePermissionsForm mode="edit" /> },
        { path: "view/:id", element: <RolePermissionsView /> },
        personalizeView("application_user_roles", null, {
          displayTitle: "Role permissions",
          configss: ROLE_COLUMNS,
        }),
      ]),
    ],
  },

  // {
  //   path: "/attendance/monthly-report",
  //   element: (
  //     <RouteProtector>
  //       <CoverLayout />
  //     </RouteProtector>
  //   ),
  //   children: [
  //     {
  //       index: true,
  //       element: <AttendanceMonthlyReportList />,
  //     },
  //     {
  //       path: "employees",
  //       element: <EmployeeMonthlyAttendanceReportList />,
  //     },
  //     {
  //       path: "interns",
  //       element: <InternMonthlyAttendanceReportList />,
  //     },
  //   ],
  // },



  {
    path: "/organization/client/assign-client-application",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      {
        index: true,
        element: <AssignClientApplication />,
      },
    ],
  },
  {
    path: "/organization/client/assign-organization-application",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      {
        index: true,
        element: <AssignOrganizationApplication />,
      },
    ],
  },
  {
    path: "/organization/client/assign-application",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      {
        index: true,
        element: <AssignClientApplication />,
      },
    ],
  },

  {
    path: "/organization/client",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<ClientList />, [
        { path: "add", element: <ClientForm mode="add" /> },
        { path: "edit/:id", element: <ClientForm mode="edit" /> },
        { path: "view/:id", element: <ClientForm mode="view" /> },
      ]),
      personalizeView(
        "clients",
        `${MAIN_URL}/api/client-management/clients?mode=1`,
        { displayTitle: "Clients", configss: CLIENT_COLUMNS }
      ),
    ],
  },
  {
    path: "/organization/client/licenses",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<ClientLicensesList />),
      personalizeView(
        "client_licenses",
        `${MAIN_URL}/api/client-management/client-licenses?mode=1`,
        { displayTitle: "Client licenses", configss: CLIENT_LICENSE_COLUMNS }
      ),
    ],
  },
  {
    path: "/organization/company-clients",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<OrganizationClientList />, [
        { path: "add", element: <OrganizationClientForm mode="add" /> },
        { path: "edit/:id", element: <OrganizationClientForm mode="edit" /> },
        { path: "view/:id", element: <OrganizationClientForm mode="view" /> },
      ]),
      personalizeView(
        "organization_clients",
        `${MAIN_URL}/api/client-management/organization-clients?mode=1`,
        {
          displayTitle: "Company clients",
          configss: ORGANIZATION_CLIENT_COLUMNS,
        }
      ),
    ],
  },


  {
    path: "/organization/application-users",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<ApplicationUserList />),
      personalizeView(
        "application_users",
        `${MAIN_URL}/api/client-management/application-users?mode=1`,
        { displayTitle: "Application users", configss: APPLICATION_USER_COLUMNS }
      ),
    ],
  },


  {
    path: "/organization/client-contacts",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<OrganizationClientContactList />),
      personalizeView(
        "organization_client_contacts",
        `${MAIN_URL}/api/client-management/client-contacts?mode=1`,
        { displayTitle: "Client contacts", configss: CLIENT_CONTACT_COLUMNS }
      ),
    ],
  },


  
  {
    path: "/organization/client-payments",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<ClientPaymentList />, [
        { path: "add", element: <ClientPaymentForm mode="add" /> },
        { path: "edit/:id", element: <ClientPaymentForm mode="edit" /> },
        { path: "view/:id", element: <ClientPaymentForm mode="view" /> },
      ]),
      personalizeView(
        "client_payments",
        `${MAIN_URL}/api/client-management/client-payments?mode=1`,
        { displayTitle: "Client payments", configss: CLIENT_PAYMENT_COLUMNS }
      ),
    ],
  },


  {
    path: "/organization/client/organizations",
    element: (
      <RouteProtector>
        <CoverLayout />
      </RouteProtector>
    ),
    children: [
      drawerList(<ClientOrganizationList />, [
        { path: "add", element: <ClientOrganizationAdd /> },
        { path: "edit/:id", element: <ClientOrganizationEdit mode="edit" /> },
        { path: "view/:id", element: <ClientOrganizationEdit mode="view" /> },
      ]),
      personalizeView(
        "organizations",
        `${MAIN_URL}/api/client-management/organizations?mode=1`,
        { displayTitle: "Client organizations", configss: ORGANIZATION_COLUMNS }
      ),
    ],
  },


 
  {
    path: "/login",
    element: <Login title={"Login"} />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword title={"Forgot Password"} />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword title={"Reset Password"} />,
  },
  {
    path: "/settings/change-password",
    element: (
      <RouteProtector>
        <ChangePassword />
      </RouteProtector>
    ),
  },
  {
    path: "/settings/update-credentials",
    element: (
      <RouteProtector>
        <UpdateCredentials />
      </RouteProtector>
    ),
  },
  // footer
  {
    path: "/features",
    element: <Features />,
  },
  {
    path: "/pricing",
    element: <Pricing />,
  },
  {
    path: "/faq",
    element: <Faq />,
  },
  {
    path: "/terms",
    element: <Terms />,
  },
  {
    path: "/privacy",
    element: <Privacy />,
  },
  {
    path: "/cookies",
    element: <Cookies />,
  },
  {
    path: "/support",
    element: <Support />,
  },
  {
    path: "/system-status",
    element: <SystemStatus />,
  },
  {
    path: "/not-authorised",
    element: <NotAuthorized />,
  },
  {
    path: "*",
    element: <PageNotFound />,
  },
]);






export default function AppRouter() {
  return <RouterProvider router={router()} />;
}
